'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { ChevronLeft, SaveAltSharp } from '@mui/icons-material';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { format } from 'date-fns';

import {
  SponsoredProductsReportsBannerList,
  useBreadcrumbs,
  useDownloadSponsoredProductsReportsBanner,
  useGetAdvertiser,
  useGetSponsoredCampaign,
  useGetSponsoredProductsReportsProduct,
} from '~/entities';
import {
  ContentTitle,
  Pagination,
  StyledContainer,
  StyledTableContainer,
} from '~/shared';
import { SponsoredProductsReportsBannerDto } from '~/types';

export type PatrocinadosRelatoriosProdutoProps = {
  advertiserId: string;
  campaignId: string;
  sku: string;
};

const REPORTS_CAMPAIGNS_PAGE_SIZE = 15;

export function PatrocinadosRelatoriosProdutoView({
  advertiserId,
  campaignId,
  sku,
}: PatrocinadosRelatoriosProdutoProps) {
  const breadcrumbs = useBreadcrumbs();

  const [reportsFilter, setReportsFilter] =
    useState<SponsoredProductsReportsBannerDto>({
      idCampaign: campaignId,
      key: sku,
      count: REPORTS_CAMPAIGNS_PAGE_SIZE,
      page: 1,
      projectAdsId: 2,
    });

  const getAdvertiserQuery = useGetAdvertiser(advertiserId);
  const getCampaignQuery = useGetSponsoredCampaign(campaignId);

  const getReportsBannerQuery =
    useGetSponsoredProductsReportsProduct(reportsFilter);
  const { isLoading, isFetching, data } = getReportsBannerQuery;

  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / REPORTS_CAMPAIGNS_PAGE_SIZE);

  const advertiserTitle = getAdvertiserQuery.data?.name ?? 'Carregando';
  const campaignTitle = getCampaignQuery.data?.name ?? 'Carregando';
  const bannerTitle = sku ?? 'Carregando';

  useEffect(() => {
    breadcrumbs.setPaths([
      { label: 'Relatórios Patrocinados', path: '/patrocinados/relatorios' },
      {
        label: advertiserTitle,
        path: `/patrocinados/relatorios/${advertiserId}`,
      },
      {
        label: campaignTitle ?? 'Carregando',
        path: `/patrocinados/relatorios/${advertiserId}/${campaignId}`,
      },
      {
        label: bannerTitle,
        path: `/patrocinados/relatorios/${advertiserId}/${campaignId}/${sku}`,
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advertiserTitle, campaignTitle]);

  const downloadReportsBannerMutation =
    useDownloadSponsoredProductsReportsBanner(res => {
      const nowFormatted = format(new Date(), 'dd-MM-yyyy HH-mm');
      const filename = `Relatório Produto Patrocinado - ${sku} - ${getCampaignQuery.data?.name ?? 'Sem Nome'} - ${nowFormatted}.xlsx`;
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      a.click();
    });

  function handleExportExcel() {
    downloadReportsBannerMutation.mutate({
      idCampaign: reportsFilter.idCampaign,
      key: reportsFilter.key,
      count: reportsFilter.count,
      page: reportsFilter.page,
      projectAdsId: 2,
    });
  }

  function nextPageHandler() {
    if (reportsFilter.page < totalPages) {
      setReportsFilter(state => ({
        ...state,
        page: state.page + 1,
      }));
    }
  }

  function prevPageHandler() {
    if (reportsFilter.page > 1) {
      setReportsFilter(state => ({
        ...state,
        page: state.page - 1,
      }));
    }
  }

  return (
    <>
      <ContentTitle
        title="Relatórios Patrocinados"
        isFetching={isLoading || isFetching}
      />

      <StyledContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Link
            href={`/patrocinados/relatorios/${advertiserId}/${campaignId}`}
            passHref
          >
            <Button variant="text" color="primary">
              <ChevronLeft />
              Voltar
            </Button>
          </Link>
          <Button
            variant="contained"
            color="primary"
            size="large"
            sx={{ textTransform: 'none' }}
            onClick={handleExportExcel}
            startIcon={
              downloadReportsBannerMutation.isPending ? (
                <CircularProgress size={15} />
              ) : (
                <SaveAltSharp />
              )
            }
            disabled={downloadReportsBannerMutation.isPending}
          >
            Baixar
          </Button>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography
            variant="h5"
            sx={theme => ({
              fontSize: theme.typography.pxToRem(18),
              fontWeight: 500,
              color: '#BDBDBD',
            })}
          >
            {getAdvertiserQuery.data?.name ?? 'Carregando...'}
          </Typography>
          <Typography
            variant="h4"
            sx={theme => ({
              fontSize: theme.typography.pxToRem(26),
              fontWeight: 500,
            })}
          >
            {getCampaignQuery.data?.name ?? 'Carregando...'}
          </Typography>
          <Typography
            variant="h3"
            color="primary"
            sx={theme => ({
              fontSize: theme.typography.pxToRem(28),
              fontWeight: 500,
              mt: 4,
              mb: 2,
            })}
          >
            {sku ?? 'Carregando...'}
          </Typography>
        </Box>

        <StyledTableContainer>
          <SponsoredProductsReportsBannerList
            getReportsBannerQuery={getReportsBannerQuery}
          />
        </StyledTableContainer>
        <Pagination
          disabled={totalPages === 1 || isLoading || total === 0}
          page={reportsFilter.page}
          count={totalPages}
          nextPage={nextPageHandler}
          prevPage={prevPageHandler}
          onChange={(_, newPage) =>
            setReportsFilter(state => ({
              ...state,
              page: newPage,
            }))
          }
        />
      </StyledContainer>
    </>
  );
}
